import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON, postJSON } from '@/lib/api'

interface AccountingSyncWork {
  id: number
  name: string
  state: 'Pending' | 'Synced' | 'Error'
  detail: string | null
  task: 'Needs mapping' | 'Drift' | 'Closed books' | 'Merge or deactivate in QuickBooks' | null
}

interface AccountingSyncWorksResponse {
  accounting_sync_works: AccountingSyncWork[]
}

export function AccountingActivity() {
  const query = useQuery({
    queryKey: ['accounting-sync-works'],
    queryFn: () => getJSON<AccountingSyncWorksResponse>('/api/v1/accounting_sync_works'),
    retry: false,
  })
  const queryClient = useQueryClient()
  const retry = useMutation({
    mutationFn: (id: number) => postJSON(`/api/v1/accounting_sync_works/${id}/retry`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounting-sync-works'] }),
  })
  const dismiss = useMutation({
    mutationFn: (id: number) => postJSON(`/api/v1/accounting_sync_works/${id}/dismiss`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounting-sync-works'] }),
  })

  if (query.isLoading) {
    return <p className="text-body-sm text-muted-foreground">Loading Accounting activity...</p>
  }
  if (query.isError || !query.data) return <Alert>Accounting activity unavailable</Alert>

  const works = query.data.accounting_sync_works
  return (
    <section id="accounting-tasks" className="p-4">
      <h2 className="text-body font-semibold">Accounting activity</h2>
      <div className="pt-3">
        {(retry.error || dismiss.error) && (
          <Alert className="mb-3">Accounting task update could not be started</Alert>
        )}
        {works.length === 0 ? (
          <p className="text-body-sm text-muted-foreground">No Accounting activity yet.</p>
        ) : (
          <ActivityTable
            works={works}
            actionPending={retry.isPending || dismiss.isPending}
            onRetry={retry.mutate}
            onDismiss={dismiss.mutate}
          />
        )}
      </div>
    </section>
  )
}

function ActivityTable({
  works,
  actionPending,
  onRetry,
  onDismiss,
}: {
  works: AccountingSyncWork[]
  actionPending: boolean
  onRetry: (id: number) => void
  onDismiss: (id: number) => void
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Record</TableHead>
          <TableHead>State</TableHead>
          <TableHead>Detail</TableHead>
          <TableHead className="w-20">
            <span className="sr-only">Actions</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {works.map((work) => (
          <TableRow key={work.id} className="group">
            <TableCell className="font-medium">{work.name}</TableCell>
            <TableCell>
              <Badge variant={syncBadgeVariant(work)}>{work.task ?? work.state}</Badge>
            </TableCell>
            <TableCell className="text-muted-foreground">{work.detail ?? '-'}</TableCell>
            <TableCell>
              {work.state === 'Error' && work.task !== 'Drift' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 px-0 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                      aria-label={`${work.name} actions`}
                    >
                      <MoreHorizontal className="h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      disabled={actionPending}
                      onSelect={() =>
                        work.task === 'Merge or deactivate in QuickBooks'
                          ? onDismiss(work.id)
                          : onRetry(work.id)
                      }
                    >
                      {work.task === 'Merge or deactivate in QuickBooks'
                        ? 'Mark resolved'
                        : 'Retry'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function syncBadgeVariant(work: AccountingSyncWork) {
  if (work.task === 'Drift' || work.state === 'Error') return 'destructive' as const
  if (work.state === 'Synced') return 'success' as const
  return 'warning' as const
}
